import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export interface BehaviorBankEntry {
    id: string;
    behaviorTypeId: string | null;
    behaviorType: string;
    name: string;
    definition: string;
    topography: string;
    function: string;
    antecedent: string;
    replacement: string;
    proactive: string;
    reactive: string;
    baseline: string;
    reductionGoal: string;
    severityIntensity: string;
    baselineDate: string | null;
    dataCollectionMethod: string;
    archived: boolean;
    archivedAt: string | null;
}

export interface BehaviorBankWritePayload {
    name?: string;
    behaviorTypeId?: string | null;
    definition?: string;
    topography?: string;
    function?: string;
    antecedent?: string;
    replacement?: string;
    proactive?: string;
    reactive?: string;
    baseline?: string;
    reductionGoal?: string;
    severityIntensity?: string;
    baselineDate?: string | null;
    dataCollectionMethod?: string;
}

interface ListOpts {
    behaviorTypeId?: string;
    includeArchived?: boolean;
}

async function fetchBehaviorBank(opts: ListOpts = {}): Promise<BehaviorBankEntry[]> {
    const params: Record<string, string> = {};
    if (opts.behaviorTypeId) params.behaviorTypeId = opts.behaviorTypeId;
    if (opts.includeArchived) params.includeArchived = '1';
    const res = await api.get<BehaviorBankEntry[]>('/behavior-bank', { params });
    return res.data;
}

export function useBehaviorBank(opts: ListOpts = {}) {
    return useQuery({
        queryKey: ['behavior-bank', opts.behaviorTypeId ?? 'all', opts.includeArchived ? 'with-archived' : 'active'] as const,
        queryFn: () => fetchBehaviorBank(opts),
        staleTime: 60_000,
    });
}

async function fetchBehaviorBankEntry(id: string): Promise<BehaviorBankEntry> {
    const res = await api.get<BehaviorBankEntry>(`/behavior-bank/${id}`);
    return res.data;
}

export function useBehaviorBankEntry(id: string | undefined) {
    return useQuery({
        queryKey: ['behavior-bank', 'entry', id] as const,
        queryFn: () => fetchBehaviorBankEntry(id!),
        enabled: !!id,
    });
}

export function useCreateBehaviorBankEntry() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: BehaviorBankWritePayload) => {
            const res = await api.post<BehaviorBankEntry>('/behavior-bank', payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['behavior-bank'] }),
    });
}

export function useUpdateBehaviorBankEntry() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: BehaviorBankWritePayload }) => {
            const res = await api.patch<BehaviorBankEntry>(`/behavior-bank/${id}`, payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['behavior-bank'] }),
    });
}

export function useDeleteBehaviorBankEntry() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/behavior-bank/${id}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['behavior-bank'] }),
    });
}

export function useRestoreBehaviorBankEntry() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const res = await api.post<BehaviorBankEntry>(`/behavior-bank/${id}/restore`);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['behavior-bank'] }),
    });
}

export function useCopyBehaviorBankToClient() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, clientId }: { id: string; clientId: string }) => {
            const res = await api.post<{ id: string }>(`/behavior-bank/${id}/copy-to-client/${clientId}`);
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['client-behaviors', vars.clientId] });
        },
    });
}
